import React from 'react';
import { Button, Flex, FlexProps, Select, Text } from '@chakra-ui/react';

export interface TablePagerProps extends Pick<FlexProps, 'mt'> {
  totalItems: number;
  pageSize: number;
  currentPage: number;
  pageSizeOptions?: readonly number[];
  /** i18n labels — pass translated strings from the parent */
  labels: {
    show: string;
    itemsUnit: string;
    range: (start: number, end: number) => string;
    prevPage: string;
    nextPage: string;
    pageSizeAriaLabel?: string;
  };
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const PAGER_BUTTON_STYLE = {
  h: '34px',
  minW: '53px',
  px: '8px',
  borderRadius: '4px',
  fontFamily: "'Open Sans', sans-serif",
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: '150%',
  letterSpacing: '0.01em',
};

const PAGER_SELECT_STYLE = {
  w: '75px',
  h: '33px',
  fontSize: '14px',
  fontFamily: "'Open Sans', sans-serif",
  letterSpacing: '0.01em',
  color: '#4F6470',
  bg: '#FFFFFF',
  borderColor: '#A8B8C2',
  borderRadius: '3px',
  _focus: { borderColor: '#A8B8C2', boxShadow: 'none' },
};

const PAGER_LABEL_STYLE = {
  fontFamily: "'Open Sans', sans-serif",
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '150%',
  letterSpacing: '0.01em',
  color: '#000000',
};

export const TablePager: React.FC<TablePagerProps> = ({
  totalItems,
  pageSize,
  currentPage,
  pageSizeOptions = [10, 20],
  labels,
  onPageChange,
  onPageSizeChange,
  mt,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = totalItems === 0 ? 0 : (safePage - 1) * pageSize;
  const endIndex = totalItems === 0 ? 0 : Math.min(startIndex + pageSize, totalItems);
  const isPrevDisabled = safePage === 1;
  const isNextDisabled = safePage === totalPages || totalItems === 0;

  return (
    <Flex w="100%" justify="center" align="center" mt={mt}>
      <Flex align="center" gap="24px" wrap="wrap" justify="center">
        {/* Page-size selector */}
        <Flex align="center" gap="17px">
          <Text {...PAGER_LABEL_STYLE}>{labels.show}</Text>
          <Select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            {...PAGER_SELECT_STYLE}
            aria-label={labels.pageSizeAriaLabel}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Select>
          <Text {...PAGER_LABEL_STYLE}>{labels.itemsUnit}</Text>
        </Flex>

        {/* Range indicator */}
        <Text {...PAGER_LABEL_STYLE}>{labels.range(startIndex + (totalItems === 0 ? 0 : 1), endIndex)}</Text>

        {/* Prev / Next */}
        <Flex align="center" gap="13px">
          <Button
            onClick={() => onPageChange(Math.max(safePage - 1, 1))}
            isDisabled={isPrevDisabled}
            {...PAGER_BUTTON_STYLE}
            bg={isPrevDisabled ? 'transparent' : '#00AAFF'}
            color={isPrevDisabled ? '#E6F7FF' : '#FFFFFF'}
            border={isPrevDisabled ? '1px solid #E6F7FF' : 'none'}
            _hover={isPrevDisabled ? { bg: 'transparent' } : { bg: '#0099E6' }}
            _active={isPrevDisabled ? { bg: 'transparent' } : { bg: '#0099E6' }}
            _disabled={{ opacity: 1, cursor: 'not-allowed', pointerEvents: 'none' }}
            aria-label={labels.prevPage}
          >
            {labels.prevPage}
          </Button>

          <Button
            onClick={() => onPageChange(Math.min(safePage + 1, totalPages))}
            isDisabled={isNextDisabled}
            {...PAGER_BUTTON_STYLE}
            bg={isNextDisabled ? 'transparent' : '#00AAFF'}
            color={isNextDisabled ? '#E6F7FF' : '#FFFFFF'}
            border={isNextDisabled ? '1px solid #E6F7FF' : 'none'}
            _hover={isNextDisabled ? { bg: 'transparent' } : { bg: '#0099E6' }}
            _active={isNextDisabled ? { bg: 'transparent' } : { bg: '#0099E6' }}
            _disabled={{ opacity: 1, cursor: 'not-allowed', pointerEvents: 'none' }}
            aria-label={labels.nextPage}
          >
            {labels.nextPage}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default TablePager;
